import React, { useState, useEffect, useCallback } from 'react';
import {
  GraduationCap, ExternalLink, RefreshCw, Users,
  CheckCircle2, XCircle, Loader2, Download, BookOpen
} from 'lucide-react';
import {
  getGCAuthUrl, getGCStatus, getGCCourses,
  getGCCourseStudents, importGCCourse,
  getMyClasses, getClassMembers
} from '../../lib/api';
import { toast } from 'sonner';
import { DemoChip } from '../ui/demo-chip';

// Canonical student join link for a class code (Q11).
const joinLink = (code) => `${window.location.origin}/join/${code}`;

export const GoogleClassroom = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [importResults, setImportResults] = useState({});
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [expandedClass, setExpandedClass] = useState(null); // join_code of expanded roster
  const [members, setMembers] = useState({}); // join_code -> member list

  const loadClasses = useCallback(async () => {
    try {
      const data = await getMyClasses();
      setClasses(Array.isArray(data) ? data : data?.classes || []);
    } catch (e) {
      console.error('Failed to load classes:', e);
    } finally {
      setClassesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const toggleRoster = async (joinCode) => {
    if (expandedClass === joinCode) { setExpandedClass(null); return; }
    setExpandedClass(joinCode);
    if (!members[joinCode]) {
      try {
        const data = await getClassMembers(joinCode);
        setMembers(prev => ({ ...prev, [joinCode]: data?.members || [] }));
      } catch (e) {
        console.error('Failed to load roster:', e);
        toast.error('Could not load the roster.');
      }
    }
  };

  // Check connection status on mount
  useEffect(() => {
    checkStatus();
  }, []);

  // The OAuth popup is opened with noopener, so it can't reach this window
  // directly. The popup lands on the frontend with ?gc_connected=true and
  // relays the result here via BroadcastChannel (see OAuthPopupRelay in App.js).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('draftflow-oauth');
    channel.onmessage = (e) => {
      if (e.data === 'gc_connected') {
        setConnected(true);
        toast.success('Google Classroom connected successfully!');
        loadCourses();
      } else if (typeof e.data === 'string' && e.data.startsWith('gc_error:')) {
        toast.error(`Google Classroom connection failed: ${e.data.slice('gc_error:'.length)}`);
      }
    };
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback for browsers without BroadcastChannel: poll status while the
  // user completes the consent screen in the popup
  const pollRef = React.useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  // Listen for successful OAuth return via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gc_connected') === 'true') {
      setConnected(true);
      toast.success('Google Classroom connected successfully!');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      loadCourses();
    } else if (params.get('gc_error')) {
      toast.error(`Google Classroom connection failed: ${params.get('gc_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    try {
      const data = await getGCStatus();
      setConnected(data.connected);
      if (data.connected) {
        loadCourses();
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    setCoursesLoading(true);
    try {
      const data = await getGCCourses();
      setCourses(data.courses || []);
    } catch (error) {
      if (error.response?.status === 401) {
        setConnected(false);
        toast.error('Google Classroom session expired. Please reconnect.');
      } else {
        toast.error('Failed to load courses.');
      }
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const data = await getGCAuthUrl();
      // Open OAuth in a new window/tab
      window.open(data.url, '_blank', 'width=600,height=700,noopener,noreferrer');

      // Poll connection status while the popup is open (max ~2 minutes)
      clearInterval(pollRef.current);
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        if (tries > 40) { clearInterval(pollRef.current); return; }
        try {
          const status = await getGCStatus();
          if (status.connected) {
            clearInterval(pollRef.current);
            setConnected((prev) => {
              if (!prev) loadCourses();
              return true;
            });
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (error) {
      toast.error('Failed to start Google authorization.');
    }
  };

  const handleSelectCourse = async (course) => {
    setSelectedCourse(course);
    setStudentsLoading(true);
    try {
      const data = await getGCCourseStudents(course.id);
      setStudents(data.students || []);
    } catch {
      toast.error('Failed to load student roster.');
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleImport = async (course) => {
    setImporting(course.id);
    try {
      const result = await importGCCourse(course.id);
      setImportResults(prev => ({ ...prev, [course.id]: result }));
      toast.success(
        `Imported "${result.class_name}" with ${result.total_students} students (${result.students_imported} new, ${result.students_existing} existing).`
      );
      loadClasses(); // the new class appears in My Classes immediately
    } catch (error) {
      const status = error.response?.status;
      toast.error(status === 401
        ? 'Your Google Classroom session expired. Reconnect and try again.'
        : "We couldn't import that course. Try again in a moment.");
    } finally {
      setImporting(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="animate-spin mx-auto mb-2" size={24} />
        Checking Google Classroom connection...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* My Classes */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-white mb-4">My Classes</h3>
        {classesLoading ? (
          <p className="text-slate-400 text-sm">Loading classes…</p>
        ) : classes.length === 0 ? (
          <div className="card-surface p-6 text-center">
            <p className="text-slate-400 text-sm">
              No classes yet. Import one from Google Classroom below — your roster comes with it.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {classes.map(cls => (
              <div key={cls.id} className="card-surface p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-medium">
                      {cls.class_name}
                      {cls.google_classroom_id && (
                        <span className="ml-2 text-xs text-slate-500">· imported from Google Classroom</span>
                      )}
                    </p>
                    {/* Join LINK is primary (Q11); raw code secondary */}
                    <p className="text-slate-400 text-sm mt-1 truncate">
                      <span className="font-mono text-teal-flow">{joinLink(cls.join_code)}</span>
                    </p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      or share code: <span className="font-mono">{cls.join_code}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`copy-join-link-${cls.join_code}`}
                      onClick={() => { navigator.clipboard.writeText(joinLink(cls.join_code)); toast.success('Join link copied! Share it with your students.'); }}
                      className="btn-teal px-3 py-1.5 rounded-lg text-sm"
                    >
                      Copy join link
                    </button>
                    <button
                      data-testid={`roster-toggle-${cls.join_code}`}
                      onClick={() => toggleRoster(cls.join_code)}
                      className="border border-border-secondary text-cream hover:border-flow-blue-hover px-3 py-1.5 rounded-lg text-sm transition-colors"
                    >
                      {expandedClass === cls.join_code ? 'Hide roster' : 'View roster'}
                    </button>
                  </div>
                </div>
                {expandedClass === cls.join_code && (
                  <div className="mt-3 border-t border-slate-700 pt-3">
                    {(members[cls.join_code] || []).length === 0 ? (
                      <p className="text-slate-500 text-sm">No students yet. Share the join code or post an assignment link in Google Classroom.</p>
                    ) : (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {(members[cls.join_code] || []).map((m, i) => (
                          <li key={i} className="text-slate-300 text-sm px-2 py-1 flex items-baseline gap-2">
                            <span>{m.full_name || m.email || 'Student'}</span>
                            {m.email && m.full_name && <span className="text-slate-500 text-xs">{m.email}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connection Status Header */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ai-accent/20 rounded-lg">
              <GraduationCap className="text-ai-accent" size={24} />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-white">Google Classroom</h2>
              <div className="flex items-center gap-2 mt-1">
                {connected ? (
                  <>
                    <CheckCircle2 className="text-green-400" size={16} />
                    <span className="text-green-400 text-sm">Connected</span>
                  </>
                ) : (
                  <>
                    <XCircle className="text-slate-500" size={16} />
                    <span className="text-slate-500 text-sm">Not Connected</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {connected ? (
            <button
              onClick={loadCourses}
              disabled={coursesLoading}
              className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <RefreshCw size={16} className={coursesLoading ? 'animate-spin' : ''} />
              Refresh Courses
            </button>
          ) : (
            <DemoChip label="Sign in to connect Google Classroom">
              <button
                onClick={handleConnect}
                className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                <ExternalLink size={16} />
                Connect Google Classroom
              </button>
            </DemoChip>
          )}
        </div>
      </div>

      {/* Not Connected State */}
      {!connected && (
        <div className="card-surface p-8 text-center">
          <GraduationCap className="text-slate-600 mx-auto mb-4" size={48} />
          <h3 className="text-white font-medium mb-2">Connect Your Google Classroom</h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto mb-6">
            Link your Google Classroom account to import courses and student rosters directly into DraftFlow.
            You will be asked to grant read-only access to your courses and rosters.
          </p>
          <DemoChip label="Sign in to connect Google Classroom">
            <button
              onClick={handleConnect}
              className="btn-teal px-6 py-3 rounded-lg inline-flex items-center gap-2"
            >
              <ExternalLink size={18} />
              Connect with Google
            </button>
          </DemoChip>
          <p className="text-slate-500 text-xs mt-4 max-w-lg mx-auto">
            DraftFlow only reads your course list and rosters when you click Import, and only
            to create your classes here. DraftFlow's use and transfer of information received
            from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank" rel="noopener noreferrer"
              className="underline hover:text-slate-300"
            >
              Google API Services User Data Policy
            </a>, including the Limited Use requirements.
          </p>
        </div>
      )}

      {/* Course List */}
      {connected && (
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BookOpen className="text-teal-flow" size={20} />
            Your Courses
          </h3>

          {coursesLoading ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} />
              Loading courses...
            </div>
          ) : courses.length === 0 ? (
            <p className="text-slate-400 text-sm py-4">No active courses found in your Google Classroom account.</p>
          ) : (
            <div className="space-y-3">
              {courses.map((course) => {
                const result = importResults[course.id];
                const isImporting = importing === course.id;
                const isSelected = selectedCourse?.id === course.id;

                return (
                  <div
                    key={course.id}
                    className={`p-4 bg-slate-900/50 rounded-lg border transition-colors ${
                      isSelected ? 'border-flow-blue/50 bg-flow-blue/5' : 'border-slate-700 hover:bg-slate-900'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => handleSelectCourse(course)}
                      >
                        <h4 className="text-white font-medium">{course.name}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          {course.section && (
                            <span className="text-slate-400 text-xs">Section: {course.section}</span>
                          )}
                          {course.room && (
                            <span className="text-slate-400 text-xs">Room: {course.room}</span>
                          )}
                          <span className="px-2 py-0.5 bg-ai-accent/20 text-ai-accent text-xs rounded-full">
                            Google Classroom
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {result ? (
                          <span className="text-green-400 text-sm flex items-center gap-1">
                            <CheckCircle2 size={14} />
                            Imported ({result.total_students} students)
                          </span>
                        ) : (
                          <button
                            onClick={() => handleImport(course)}
                            disabled={isImporting}
                            className="btn-purple px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                            title="Import this course into DraftFlow"
                          >
                            {isImporting ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Download size={14} />
                                Import
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Import result details */}
                    {result && (
                      <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                        <p className="text-green-400">
                          Class created: <span className="text-white font-medium">{result.class_name}</span>
                          {result.join_code && (
                            <> (Join code: <code className="text-teal-flow">{result.join_code}</code>)</>
                          )}
                        </p>
                        <p className="text-slate-400 mt-1">
                          {result.students_imported} new students imported, {result.students_existing} already in DraftFlow
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Student Roster Panel */}
      {connected && selectedCourse && (
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="text-ai-accent" size={20} />
            Roster: {selectedCourse.name}
            {!studentsLoading && students.length > 0 && (
              <span className="text-slate-400 text-sm font-normal">({students.length} students)</span>
            )}
          </h3>

          {studentsLoading ? (
            <div className="text-center py-6 text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={20} />
              Loading roster...
            </div>
          ) : students.length === 0 ? (
            <p className="text-slate-400 text-sm">No students found in this course.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {students.map((student, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-900/50">
                  {student.photo_url ? (
                    <img
                      src={student.photo_url}
                      alt=""
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                      {(student.name || student.email || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-white text-sm">{student.name || 'Unknown'}</p>
                    <p className="text-slate-500 text-xs">{student.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
